import { z } from 'zod';

// ============================================================================
// ENUMS
// ============================================================================

export const jobTypeSchema = z.enum([
  'PRINT_3D',
  'PRINT_2D',
  'PRINT_UV',
  'PRINT_DYESUB',
  'CUT_LASER',
  'CUT_DIE',
]);

export const jobStatusSchema = z.enum([
  'DRAFT',
  'READY',
  'PENDING',
  'QUEUED',
  'IN_PROGRESS',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
]);

export const jobTaskStatusSchema = z.enum([
  'PENDING',
  'QUEUED',
  'IN_PROGRESS',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
]);

// ============================================================================
// JOB SCHEMAS
// ============================================================================

/**
 * Create job schema
 */
export const createJobSchema = z.object({
  jobType: jobTypeSchema,
  name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
  description: z.string().max(1000, 'Description too long').optional(),
  quantity: z.number().int().min(1).max(100).default(1),
  dueDate: z.string().datetime().optional(),
  modelId: z.string().uuid().optional(),
  modelFileId: z.string().uuid().optional(),
});

export type CreateJobInput = z.infer<typeof createJobSchema>;

/**
 * Update job schema
 */
export const updateJobSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(1000).optional(),
  status: jobStatusSchema.optional(),
  statusMessage: z.string().max(500).optional(),
  dueDate: z.string().datetime().nullable().optional(),
  modelId: z.string().uuid().nullable().optional(),
  modelFileId: z.string().uuid().nullable().optional(),
});

export type UpdateJobInput = z.infer<typeof updateJobSchema>;

/**
 * Add tasks to job schema
 */
export const addTasksToJobSchema = z.object({
  additionalQuantity: z.number().int().min(1).max(100),
});

export type AddTasksToJobInput = z.infer<typeof addTasksToJobSchema>;

/**
 * Cancel job schema
 */
export const cancelJobSchema = z.object({
  reason: z.string().max(500, 'Reason too long').optional(),
});

export type CancelJobInput = z.infer<typeof cancelJobSchema>;

/**
 * Job query schema
 */
export const jobQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25).optional(),
  search: z.string().max(200).optional(),
  jobType: jobTypeSchema.optional(),
  status: jobStatusSchema.optional(),
  createdBy: z.string().uuid().optional(),
  modelId: z.string().uuid().optional(),
  dueDateFrom: z.string().datetime().optional(),
  dueDateTo: z.string().datetime().optional(),
  sortBy: z
    .enum(['name', 'createdAt', 'updatedAt', 'dueDate', 'progress'])
    .default('createdAt')
    .optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc').optional(),
});

export type JobQuery = z.infer<typeof jobQuerySchema>;

// ============================================================================
// TASK SCHEMAS
// ============================================================================

/**
 * Update task schema
 */
export const updateTaskSchema = z.object({
  status: jobTaskStatusSchema.optional(),
  statusMessage: z.string().max(500).optional(),
  progress: z.number().min(0).max(100).optional(),
  estimatedTimeSeconds: z.number().min(0).optional(),
  estimatedCost: z.number().min(0).optional(),
  actualCost: z.number().min(0).optional(),
});

export type UpdateJobTaskInput = z.infer<typeof updateTaskSchema>;

/**
 * Cancel task schema
 */
export const cancelTaskSchema = z.object({
  reason: z.string().max(500, 'Reason too long').optional(),
});

export type CancelTaskInput = z.infer<typeof cancelTaskSchema>;

/**
 * Task query schema
 */
export const taskQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25).optional(),
  jobId: z.string().uuid().optional(),
  status: jobTaskStatusSchema.optional(),
  sortBy: z
    .enum(['taskNumber', 'createdAt', 'startedAt', 'completedAt', 'progress'])
    .default('taskNumber')
    .optional(),
  sortOrder: z.enum(['asc', 'desc']).default('asc').optional(),
});

export type JobTaskQuery = z.infer<typeof taskQuerySchema>;

// ============================================================================
// NOTE SCHEMAS
// ============================================================================

/**
 * Create job note schema
 */
export const createJobNoteSchema = z.object({
  content: z.string().min(1, 'Note content is required').max(5000, 'Note too long'),
  isPinned: z.boolean().default(false).optional(),
});

export type CreateJobNoteInput = z.infer<typeof createJobNoteSchema>;

/**
 * Create task note schema
 */
export const createTaskNoteSchema = z.object({
  content: z.string().min(1, 'Note content is required').max(5000, 'Note too long'),
  isPinned: z.boolean().default(false).optional(),
});

export type CreateJobTaskNoteInput = z.infer<typeof createTaskNoteSchema>;

/**
 * Job notes query schema
 */
export const jobNotesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25).optional(),
  sortBy: z.enum(['createdAt', 'updatedAt']).default('createdAt').optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc').optional(),
});

export type JobNotesQuery = z.infer<typeof jobNotesQuerySchema>;

// ============================================================================
// CHANGE HISTORY SCHEMAS
// ============================================================================

/**
 * Job history query schema
 */
export const jobHistoryQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25).optional(),
  sortBy: z.enum(['createdAt']).default('createdAt').optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc').optional(),
});

export type JobHistoryQuery = z.infer<typeof jobHistoryQuerySchema>;

// ============================================================================
// JOB OBJECT SCHEMAS (3D Printing)
// ============================================================================

/**
 * Create job object schema
 */
export const createJobObjectSchema = z.object({
  plateObjectId: z.string().uuid(),
  quantity: z.number().int().min(1).max(100).default(1).optional(),
  overrideColorHex: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color')
    .optional(),
});

/**
 * Assign filament to job object schema
 */
export const assignFilamentSchema = z.object({
  tenantFilamentId: z.string().uuid(),
  colorRegion: z.string().max(100).optional(),
  sequenceOrder: z.number().int().min(0).optional(),
  estimatedGrams: z.number().min(0).optional(),
});
